import { useEffect, useState } from 'react'
import { Capacitor, registerPlugin } from '@capacitor/core'
import { supabase } from '../lib/supabaseClient'
import { encryptField } from '../lib/secureFields'

const Transacciones = registerPlugin('Transacciones')

const CATEGORIAS_GASTO = [
  'Arriendo', 'Administración', 'Agua', 'Luz', 'Gas', 'Internet',
  'Mercado', 'Alimentación', 'Transporte', 'Tarjetas de crédito', 'Créditos',
  'EPS', 'Medicina prepagada', 'Celular', 'Streaming', 'Ropa', 'Salidas', 'Viajes', 'Otro'
]
const CATEGORIAS_INGRESO = [
  'Salario', 'Honorarios', 'Negocio propio', 'Horas extra', 'Comisiones',
  'Bonificaciones', 'Primas', 'Cesantías', 'Regalos o ayudas familiares', 'Otro'
]

function hoy() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function formatear(valor) {
  const digitos = String(valor).replace(/\D/g, '')
  return digitos ? new Intl.NumberFormat('es-CO').format(digitos) : ''
}

export default function TransaccionDetectada() {
  const [tx, setTx] = useState(null)
  const [monto, setMonto] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [categoria, setCategoria] = useState('Otro')
  const [fecha, setFecha] = useState(hoy())
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  function abrir(t) {
    setTx(t)
    setMonto(formatear(Math.round(t.monto)))
    setDescripcion(t.descripcion || '')
    setCategoria('Otro')
    setFecha(hoy())
    setError('')
  }

  function cerrar() {
    setTx(null)
    setError('')
  }

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    let handle = null
    let cancelado = false

    Transacciones.obtenerPendiente()
      .then((r) => { if (!cancelado && r?.hay) abrir(r.tx) })
      .catch(() => {})

    Transacciones.addListener('transaccionDetectada', abrir).then((h) => {
      if (cancelado) h.remove()
      else handle = h
    })

    return () => {
      cancelado = true
      if (handle) handle.remove()
    }
  }, [])

  async function guardar() {
    setError('')
    const amount = parseInt(monto.replace(/\D/g, ''), 10)
    if (!amount || amount <= 0) { setError('El monto debe ser mayor a 0'); return }
    if (descripcion.trim().length > 200) { setError('La descripción no puede tener más de 200 caracteres'); return }

    setGuardando(true)
    try {
      const { data } = await supabase.auth.getUser()
      const user = data?.user
      if (!user) { setError('Inicia sesión en Calypso para guardar este movimiento'); return }

      const encCategoria = await encryptField(categoria)
      const encDescripcion = descripcion.trim() ? await encryptField(descripcion.trim()) : null

      const { error: errInsert } = await supabase.from('transactions').insert([{
        user_id: user.id,
        type: tx.tipo === 'ingreso' ? 'income' : 'expense',
        amount,
        category: encCategoria,
        description: encDescripcion,
        date: fecha
      }])

      if (errInsert) { setError('Error al guardar'); return }

      cerrar()
      window.dispatchEvent(new Event('calypso:movimiento-agregado'))
    } catch {
      setError('Error inesperado al guardar')
    } finally {
      setGuardando(false)
    }
  }

  if (!tx) return null

  const esIngreso = tx.tipo === 'ingreso'
  const color = esIngreso ? '#22c55e' : '#ef4444'
  const categorias = esIngreso ? CATEGORIAS_INGRESO : CATEGORIAS_GASTO

  const campo = {
    padding: '10px 12px', borderRadius: 10, fontSize: 14, outline: 'none',
    border: '1px solid #333', background: '#1a1a1a', color: '#fff', width: '100%', boxSizing: 'border-box'
  }

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      background: 'rgba(0,0,0,0.65)', display: 'flex', justifyContent: 'center',
      alignItems: 'center', zIndex: 300, padding: 16, boxSizing: 'border-box'
    }}>
      <div style={{
        background: '#111', padding: 22, borderRadius: 20, width: '100%', maxWidth: 380,
        display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '90vh', overflowY: 'auto'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 34 }}>{esIngreso ? '💸' : '🧾'}</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#fff', marginTop: 4 }}>
            {esIngreso ? '¿Agregar este ingreso?' : '¿Agregar este gasto?'}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
            Lo detectamos en una notificación. Revísalo antes de guardar.
          </div>
        </div>

        <input
          type="text"
          inputMode="numeric"
          value={monto}
          onChange={(e) => setMonto(formatear(e.target.value))}
          style={{ ...campo, color, fontWeight: 800, fontSize: 22, textAlign: 'center' }}
        />

        <input
          type="text"
          value={descripcion}
          maxLength={200}
          placeholder="Descripción"
          onChange={(e) => setDescripcion(e.target.value)}
          style={campo}
        />

        <select value={categoria} onChange={(e) => setCategoria(e.target.value)} style={campo}>
          {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={campo} />

        {error && <div style={{ fontSize: 12, color: '#f87171', textAlign: 'center' }}>⚠️ {error}</div>}

        <button
          onClick={guardar}
          disabled={guardando}
          style={{
            padding: 13, borderRadius: 12, border: 'none', background: color, color: '#fff',
            fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: guardando ? 0.7 : 1
          }}
        >
          {guardando ? 'Guardando...' : 'Agregar'}
        </button>
        <button
          onClick={cerrar}
          disabled={guardando}
          style={{
            padding: 13, borderRadius: 12, border: 'none', background: '#333', color: '#fff',
            fontWeight: 600, fontSize: 15, cursor: 'pointer'
          }}
        >
          Cancelar
        </button>
      </div>
    </div>
  )
}
